import { getReadSignedUrl, getSignedUrl } from "frontend/api";
import { memo, useEffect, useRef, useState } from "react";
import { File } from "shared/datamodel/schemas/file";
import { Image, Rect } from "react-konva";
import { useAtomValue } from "jotai";
import { documentIdAtom, isThumbnailExportModeAtom } from "state-atoms/stage-atoms";
import { FileMetadata, FileMetadataType, FileUploadState } from "shared/datamodel/schemas/metadata";
import { M } from "shared/datamodel/mutators";
import { Reflect } from "@workcanvas/reflect/client";
import { useSubscribe } from "replicache-react";
import { canvasMetadataPrefix } from "shared/util/utils";
import { ReadTransaction } from "@workcanvas/reflect";
import { useTrackRenderCountForTests } from "utils/tests/use-track-render-count-for-tests";
import FileElementPlaceholder from "./file-element-placeholder";

export type FilesMetadata = Record<string, FileMetadata>;

const fetchImage = async (url: string, crossOrigin: string | null = "anonymous") => {
  const img = document.createElement("img");
  crossOrigin && (img.crossOrigin = crossOrigin);
  const result = new Promise<HTMLImageElement>((resolve, reject) => {
    img.onload = () => resolve(img);
    img.onerror = reject;
  });
  img.src = url;
  return result;
};

type FileCanvasElementProps = {
  id: string;
  element: File;
  metadata?: any;
  rep: Reflect<M> | null | undefined;
};

function FileCanvasElement({ id, element, metadata, rep }: FileCanvasElementProps) {
  useTrackRenderCountForTests(id);
  const { documentId } = useAtomValue(documentIdAtom);
  const isThumbnailExportMode = useAtomValue(isThumbnailExportModeAtom);

  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const [imgStatus, setImgStatus] = useState("loading");
  const alreadyCompleted = useRef(false);

  const { fileId } = element;

  const fileSubscription = useSubscribe(
    rep,
    async (tx: ReadTransaction) => await tx.get(`${canvasMetadataPrefix + FileMetadataType}-${fileId}`),
    null,
    [fileId]
  ) as FileMetadata | undefined;

  const fileMetadataEntry =
    metadata && fileId && metadata[fileId as keyof FileMetadata]
      ? metadata[fileId as keyof FileMetadata]
      : fileSubscription;

  useEffect(() => {
    async function getFile(fileId: string | number) {
      let response: any;

      if (alreadyCompleted.current) {
        return;
      }

      try {
        if (Number.isFinite(Number(fileId))) {
          alreadyCompleted.current = true;
          response = await getSignedUrl({ action: "read", fileId: Number(fileId), documentId });
        } else if (fileMetadataEntry?.uploadState === FileUploadState.Completed || fileMetadataEntry?.url) {
          alreadyCompleted.current = true;
          response = await getReadSignedUrl(fileMetadataEntry.url);
        } else {
          return;
        }

        const img = await fetchImage(response.url);
        setImg(img);
        setImgStatus("loaded");
      } catch (e) {
        console.error("Failed to fetch image", e);
        setImgStatus("failed");
      }
    }
    fileId && getFile(fileId);
  }, [fileId, fileMetadataEntry, documentId]);

  function renderThumbnail() {
    const image = img as HTMLImageElement;
    return <Rect fill="#000000" opacity={0.4} width={image.naturalWidth} height={image.naturalHeight} />;
  }

  switch (imgStatus) {
    case "loaded": {
      return (
        <>
          <Image image={img as HTMLImageElement} visible={!isThumbnailExportMode} />
          {isThumbnailExportMode && renderThumbnail()}
        </>
      );
    }
    case "loading": {
      return <FileElementPlaceholder element={element} isThumbnailExportMode={isThumbnailExportMode} />;
    }
    default: {
      return null;
    }
  }
}

const MemoizedFileElement = memo(FileCanvasElement);
export default MemoizedFileElement;
